import { FormApi, useField, useSelector } from '@tanstack/vue-form'
import type { StandardSchemaV1, StandardSchemaV1Issue } from '@tanstack/vue-form'
import { computed, onMounted, onScopeDispose, shallowRef, watch } from 'vue'
import type { ComputedRef } from 'vue'
import type { z } from 'zod'
import type { FieldHandle } from '~/interfaces/FieldHandle'
import { useNuxtApp } from '#imports'

import { fieldErrorMessage } from '~/utils/fieldErrorMessage'
import { clearFormBlurError } from '~/utils/clearFormBlurError'
import { hasMetaErrors } from '~/utils/hasMetaErrors'
import { subscribeFormEditClearing } from '~/utils/subscribeFormEditClearing'
import { submitFormClearingStaleVerdicts } from '~/utils/submitFormClearingStaleVerdicts'
import type { Logger } from '~/interfaces/Logger'
import type { FieldServerMessage } from '~/types/FieldServerMessage'
import type { ApiFieldIssue } from '~/services/api/ApiFieldIssue'
import { recaptchaTokenErrorMessage } from '~/composables/recaptchaTokenErrorMessage'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { FormProblemState } from '~/services/forms/FormProblemState'
import { RecaptchaTokenError } from '~/utils/RecaptchaTokenError'
import { formTelemetry } from '~/services/forms/formTelemetry'

type FieldMap<TFormData> = { [K in keyof TFormData]: FieldHandle<TFormData[K]> }

const issueCode = (issue: unknown): string =>
  issue !== null && typeof issue === 'object' && 'code' in issue && typeof issue.code === 'string'
    ? issue.code
    : 'unknown'

const ownsIssue = (admitted: WeakSet<object>, issue: unknown): boolean =>
  issue !== null && typeof issue === 'object' && admitted.has(issue)

const hasOwnedIssues = (admitted: WeakSet<object>, errors: unknown): boolean =>
  Array.isArray(errors) && errors.some((issue) => ownsIssue(admitted, issue))

const refreshOwnedIssues = (
  admitted: WeakSet<object>,
  previous: unknown,
  replacement: readonly StandardSchemaV1Issue[] | undefined
): unknown => {
  if (!Array.isArray(previous)) return previous
  const issues: readonly unknown[] = previous
  const errors: unknown[] = []
  let replaced = false
  for (const issue of issues) {
    if (ownsIssue(admitted, issue)) {
      if (!replaced) errors.push(...(replacement ?? []))
      replaced = true
    } else {
      errors.push(issue)
    }
  }
  if (!replaced) return previous
  return errors.length > 0 ? errors : undefined
}

const refreshRootErrors = (
  admitted: WeakSet<object>,
  previous: unknown,
  occupied: ReadonlySet<string>,
  replacement: Record<string, StandardSchemaV1Issue[]> | undefined
): Map<string, unknown> | undefined => {
  if (previous === null || typeof previous !== 'object' || Array.isArray(previous)) return undefined
  const entries: [string, unknown][] = Object.entries(previous)
  const errors = new Map(entries)
  let changed = false
  for (const [path, held] of entries) {
    if (!hasOwnedIssues(admitted, held)) continue
    const refreshed = refreshOwnedIssues(
      admitted,
      held,
      occupied.has(path) ? replacement?.[path] : undefined
    )
    if (refreshed === undefined) errors.delete(path)
    else errors.set(path, refreshed)
    changed = true
  }
  return changed ? errors : undefined
}

interface UseTanstackFormOptions<TFormData extends object> {
  defaultValues: TFormData
  /**
   * Schema wraps `.transform()`/`.superRefine()` around the *generated*
   * backend contract schema -- see app/components/dashboard/forms/MenuForm.vue
   * for the established pattern. Never a hand-duplicated validation schema.
   */
  schema: z.ZodType<unknown, TFormData>
  onSubmit: (value: TFormData) => Promise<void> | void
  /** Identifies this form in validation telemetry. */
  formId: string
  /** Receives `validation_failed` and unmapped-server-issue telemetry; omitted logs nothing. */
  logger?: Logger
  /** Counts controls in error for the live banner count, given the field-meta count; for errors kept outside field meta. */
  countInvalidControls?: (fieldMetaErrorCount: number) => number
}

/**
 * Shared TanStack Form wiring for every form in this app. Owns: per-field
 * `useField` registration (via `fields`, not a whole-form `useSelector` --
 * that pattern caused a real reentrant-validation bug, see git history for
 * APP-1185), error message extraction, error clearing, and submit-attempt
 * gating. Callers own their own schema, template bindings, and
 * submit/server-error-mapping logic.
 */
export const useTanstackForm = <TFormData extends object>(
  options: UseTanstackFormOptions<TFormData>
) => {
  let activeAdmission = {
    source: options.schema,
    issues: new WeakSet<object>(),
    refreshable: false
  }
  const validationSchema: StandardSchemaV1<TFormData, unknown> = {
    '~standard': {
      version: 1,
      vendor: options.schema['~standard'].vendor,
      validate: (value) => {
        const admission = activeAdmission
        const result = admission.source['~standard'].validate(value)
        admission.refreshable = !(result instanceof Promise)
        if (!(result instanceof Promise)) {
          for (const issue of result.issues ?? []) admission.issues.add(issue)
        }
        return result
      }
    }
  }

  let submitBlocked = false
  const form = new FormApi({
    defaultValues: options.defaultValues,
    formId: options.formId,
    onSubmit: async ({ value }: { value: TFormData; meta: undefined }) => {
      await options.onSubmit(value)
    },
    onSubmitInvalid: () => {
      submitBlocked = true
    },
    validators: {
      // The same schema drives both phases on purpose: a field that fails on
      // exit must fail on submit for the same reason, and a second
      // blur-specific schema would be a second source of truth to keep in
      // step. Submit remains the final gate -- this only moves the first
      // verdict earlier, to the moment the user leaves the field.
      onBlur: validationSchema,
      onSubmit: validationSchema
    }
  })

  /** Replaces the drawer's submit-only contract while retaining native asynchronous submit scheduling. */
  const setSubmitSchema = (source: z.ZodType<unknown, TFormData>): void => {
    activeAdmission = { source, issues: new WeakSet<object>(), refreshable: false }
    form.update({ ...form.options, validators: { onSubmitAsync: validationSchema } })
  }

  onMounted(() => {
    onScopeDispose(form.mount())
  })

  const fieldNames = Object.keys(options.defaultValues) as (keyof TFormData & string)[]

  const fields = {} as FieldMap<TFormData>
  for (const name of fieldNames) {
    ;(fields as Record<string, unknown>)[name] = useField({ form, name })
  }

  const submissionAttempts = useSelector(form.store, (state) => state.submissionAttempts)
  const fieldMetas = useSelector(form.store, (state) => state.fieldMeta)
  const isFormValid = useSelector(form.store, (state) => state.isValid)
  const isSubmitting = useSelector(form.store, (state) => state.isSubmitting)

  const { $i18n } = useNuxtApp()
  watch($i18n.locale, () => {
    if (!activeAdmission.refreshable) return
    const previousFieldMeta = form.state.fieldMeta
    const previousErrorMap = form.state.errorMap
    const occupied = { onBlur: new Set<string>(), onSubmit: new Set<string>() }
    const phases: readonly ['onBlur', 'onSubmit'] = ['onBlur', 'onSubmit']

    for (const path in previousFieldMeta) {
      const meta = form.getFieldMeta(path)
      for (const phase of phases) {
        if (hasOwnedIssues(activeAdmission.issues, meta?.errorMap[phase])) occupied[phase].add(path)
      }
    }

    const parsed =
      occupied.onBlur.size + occupied.onSubmit.size > 0
        ? form.parseValuesWithSchema(validationSchema)
        : undefined

    for (const path in previousFieldMeta) {
      const hadBlur = occupied.onBlur.has(path)
      const hadSubmit = occupied.onSubmit.has(path)
      if (!hadBlur && !hadSubmit) continue
      form.setFieldMeta(path, (meta) => ({
        ...meta,
        errorMap: {
          ...meta.errorMap,
          ...(hadBlur
            ? {
                onBlur: refreshOwnedIssues(
                  activeAdmission.issues,
                  meta.errorMap.onBlur,
                  parsed?.fields[path]
                )
              }
            : {}),
          ...(hadSubmit
            ? {
                onSubmit: refreshOwnedIssues(
                  activeAdmission.issues,
                  meta.errorMap.onSubmit,
                  parsed?.fields[path]
                )
              }
            : {})
        }
      }))
    }

    for (const phase of phases) {
      const errors = refreshRootErrors(
        activeAdmission.issues,
        previousErrorMap[phase],
        occupied[phase],
        parsed?.form
      )
      if (errors !== undefined) {
        form.setErrorMap({ [phase]: errors.size > 0 ? Object.fromEntries(errors) : undefined })
      }
    }
  })

  const getFieldErrors = (field: keyof TFormData): string[] =>
    fields[field].state.meta.errors.map(fieldErrorMessage)

  /**
   * Whether this field has been checked at all: exited after an edit, or
   * submitted. A field that was only focused and left (blurred but never
   * dirty) stays silent -- an untouched field must not read as erroneous
   * before the user acted on it.
   */
  const isFieldValidated = (field: keyof TFormData): boolean =>
    submissionAttempts.value > 0 ||
    (fields[field].state.meta.isBlurred && fields[field].state.meta.isDirty)

  const getFieldError = (field: keyof TFormData): string => {
    // Gated on having been checked, not on submit alone: an untouched field
    // must stay quiet, and so must a field the user merely passed through --
    // only an edited-and-exited field or a submit attempt reveals a verdict.
    if (!isFieldValidated(field)) return ''
    return [...new Set(getFieldErrors(field))].join(' ')
  }

  const clearFieldError = (field: keyof TFormData): void => {
    fields[field].api.setMeta((meta) => ({ ...(meta ?? fields[field].state.meta), errorMap: {} }))
  }

  /** For `formValidation.applyProblemFieldErrors`'s callback parameter; no messages empties the field's server slot. */
  const setFieldServerErrors = (field: keyof TFormData, messages: FieldServerMessage[]): void => {
    fields[field].api.setMeta((meta) => ({
      ...(meta ?? fields[field].state.meta),
      errorMap: { ...meta?.errorMap, onServer: messages.length > 0 ? messages : undefined }
    }))
  }

  const stopEditClearing = subscribeFormEditClearing(
    form,
    options.defaultValues,
    () => form.store.state.values,
    clearFieldError
  )
  onScopeDispose(stopEditClearing)

  /** Every field path in error (nested and pathless included) with its distinct issue codes, for telemetry only -- never values. */
  const collectFieldIssues = (): readonly Readonly<{ field: string; codes: readonly string[] }>[] =>
    Object.entries(form.state.fieldMeta).flatMap(([path, meta]) =>
      hasMetaErrors(meta) ? [{ codes: [...new Set(meta.errors.map(issueCode))], field: path }] : []
    )

  const countFieldMetaErrors = (): number =>
    Object.values(fieldMetas.value).filter(hasMetaErrors).length

  const countInvalidControls = (): number =>
    options.countInvalidControls?.(countFieldMetaErrors()) ?? countFieldMetaErrors()

  /** Distinct pathless (form-level) schema messages: no control shows them, so a client banner lists them. */
  const formLevelMessages = (): readonly string[] =>
    Object.entries(fieldMetas.value).flatMap(([path, meta]) =>
      path === '' && hasMetaErrors(meta) ? [...new Set(meta.errors.map(fieldErrorMessage))] : []
    )

  const presentLive = (state: FormProblemState): FormProblemState =>
    state.kind === 'client'
      ? FormProblemState.withLiveFieldCount(state, countInvalidControls, formLevelMessages)
      : FormProblemState.withLiveFieldCount(state, countInvalidControls)

  const t = usePresentationTranslator()
  const problemState = shallowRef<FormProblemState | null>(null)
  const liveProblem = shallowRef<FormProblemState | null>(null)
  const setProblem = (state: FormProblemState | null): void => {
    problemState.value = state
    liveProblem.value = state === null ? null : presentLive(state)
  }

  /** The verdict of the last attempt, one object per setProblem whose field count reads live, so a falling count never reads as a new verdict. */
  const problem: ComputedRef<FormProblemState | null> = computed(() => liveProblem.value)

  /** Every submit starts with no verdict; a submit the schema blocked that raised none of its own reads as "Fix N fields". */
  const submitOnce = async (): Promise<void> => {
    setProblem(null)
    submitBlocked = false
    try {
      await submitFormClearingStaleVerdicts(form, fieldNames)
    } catch (err) {
      if (err instanceof RecaptchaTokenError) {
        options.logger?.error('handleSubmit', { formId: options.formId, reason: err.reason })
        setProblem(FormProblemState.fromMessage(() => recaptchaTokenErrorMessage(err, t), null))
        return
      }
      throw err
    }
    if (submitBlocked) {
      formTelemetry.validationFailed(options.logger, options.formId, collectFieldIssues())
      setProblem(
        problemState.value ?? FormProblemState.fromClientValidation(countInvalidControls())
      )
    }
  }

  let pendingSubmit: Promise<void> | null = null
  /** A submit requested while one is in flight joins it instead of starting a second. */
  const handleSubmit = (): Promise<void> => {
    pendingSubmit ??= submitOnce().finally(() => {
      pendingSubmit = null
    })
    return pendingSubmit
  }

  /**
   * The blur binding a control must wire for exit validation to run at all.
   * Handed out from here so no form writes its own.
   */
  const handleFieldBlur = (field: keyof TFormData): void => {
    fields[field].api.handleBlur()
  }

  const logUnmappedServerIssues = (
    issues: readonly ApiFieldIssue[],
    requestId: string | null
  ): void => {
    formTelemetry.unmappedServerIssues(options.logger, options.formId, issues, requestId)
  }

  return {
    form,
    fields,
    submissionAttempts,
    isFieldValidated,
    handleFieldBlur,
    isFormValid,
    isSubmitting,
    getFieldErrors,
    getFieldError,
    clearFieldError,
    clearFormBlurError: () => {
      clearFormBlurError(form)
    },
    logUnmappedServerIssues,
    problem,
    setProblem,
    setFieldServerErrors,
    setSubmitSchema,
    handleSubmit
  }
}
